import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CarteV2Service from '../../../services/CarteV2Service';

/**
 * Dashboard de statistiques de production avec KPIs et métriques
 */
const ProductionDashboard = ({ countryCode = null }) => {
    const { t } = useTranslation();
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        countryCode: countryCode,
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 derniers jours
        endDate: new Date().toISOString().split('T')[0]
    });
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        loadStatistics();

        return () => {
            isMountedRef.current = false;
        };
    }, [filters]);

    const loadStatistics = async () => {
        try {
            setLoading(true);
            setError(null);
            const stats = await CarteV2Service.getProductionStatisticsDetailed(filters);

            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setStatistics(stats);
            }
        } catch (err) {
            if (isMountedRef.current) {
                setError(t('productionDashboard.errors.loadingFailed'));
                console.error(err);
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const renderKPICard = (title, value, icon, variant = 'primary', subtitle = null) => (
        <Col md={3} className="mb-3">
            <Card className={`border-${variant}`}>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="text-muted mb-2">{title}</h6>
                            <h3 className={`mb-0 text-${variant}`}>{value}</h3>
                            {subtitle && <small className="text-muted">{subtitle}</small>}
                        </div>
                        <div>
                            <i className={`fa ${icon} fa-3x text-${variant}`} style={{ opacity: 0.3 }}></i>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">{t('productionDashboard.loadingStats')}</p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (!statistics) {
        return <Alert variant="warning">{t('productionDashboard.noStatsAvailable')}</Alert>;
    }

    return (
        <div className="production-dashboard">
            {/* Filtres */}
            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-end">
                        <Col md={3}>
                            <div className="form-group">
                                <label className="form-label">{t('productionDashboard.filters.country')}</label>
                                <select
                                    className="form-select"
                                    value={filters.countryCode || ''}
                                    onChange={(e) => handleFilterChange('countryCode', e.target.value || null)}
                                >
                                    <option value="">{t('productionDashboard.filters.allCountries')}</option>
                                    <option value="CI">{t('productionDashboard.countries.CI')}</option>
                                    <option value="CD">{t('productionDashboard.countries.CD')}</option>
                                    <option value="CM">{t('productionDashboard.countries.CM')}</option>
                                    <option value="SN">{t('productionDashboard.countries.SN')}</option>
                                    <option value="BF">{t('productionDashboard.countries.BF')}</option>
                                </select>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="form-group">
                                <label className="form-label">{t('productionDashboard.filters.startDate')}</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="form-group">
                                <label className="form-label">{t('productionDashboard.filters.endDate')}</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* KPIs Principaux */}
            <Row>
                {renderKPICard(
                    t('productionDashboard.kpis.totalCards') || 'Total Cards',
                    statistics.totalCards || 0,
                    'fa-id-card',
                    'primary',
                    t('productionDashboard.kpis.allCards') || 'All cards'
                )}
                {renderKPICard(
                    t('productionDashboard.kpis.completedCards') || 'Completed',
                    statistics.completedCards || 0,
                    'fa-check-circle',
                    'success',
                    `${statistics.totalCards > 0 ? ((statistics.completedCards / statistics.totalCards) * 100).toFixed(1) : 0}%`
                )}
                {renderKPICard(
                    t('productionDashboard.kpis.pendingCards') || 'Pending',
                    statistics.pendingCards || 0,
                    'fa-clock',
                    'warning',
                    `${statistics.totalCards > 0 ? ((statistics.pendingCards / statistics.totalCards) * 100).toFixed(1) : 0}%`
                )}
                {renderKPICard(
                    t('productionDashboard.kpis.failedCards') || 'Failed',
                    statistics.failedCards || 0,
                    'fa-exclamation-triangle',
                    'danger',
                    `${statistics.totalCards > 0 ? ((statistics.failedCards / statistics.totalCards) * 100).toFixed(1) : 0}%`
                )}
            </Row>

            {/* Statistiques par Étape de Production */}
            {statistics.stepStatistics && Object.keys(statistics.stepStatistics).length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-info text-white">
                        <h5 className="mb-0">
                            <i className="fa fa-tasks me-2"></i>
                            {t('productionDashboard.sections.stepStatistics') || 'Production Steps Statistics'}
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            {Object.entries(statistics.stepStatistics).map(([step, count]) => {
                                const percentage = statistics.totalCards > 0 ? ((count / statistics.totalCards) * 100).toFixed(1) : 0;
                                return (
                                    <Col md={3} key={step} className="mb-3">
                                        <div className="text-center p-3 border rounded shadow-sm">
                                            <h6 className="text-muted mb-2">{step}</h6>
                                            <h3 className="mb-1 text-primary">{count}</h3>
                                            <div className="progress mt-2" style={{ height: '8px' }}>
                                                <div
                                                    className="progress-bar bg-info"
                                                    role="progressbar"
                                                    style={{ width: `${percentage}%` }}
                                                    aria-valuenow={percentage}
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                ></div>
                                            </div>
                                            <small className="text-muted">{percentage}%</small>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Statistiques par Pays */}
            {statistics.countryStatistics && Object.keys(statistics.countryStatistics).length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-primary text-white">
                        <h5 className="mb-0">
                            <i className="fa fa-globe me-2"></i>
                            {t('productionDashboard.sections.countryDistribution') || 'Country Distribution'}
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            {Object.entries(statistics.countryStatistics).map(([country, count]) => {
                                const percentage = statistics.totalCards > 0 ? ((count / statistics.totalCards) * 100).toFixed(1) : 0;
                                return (
                                    <Col md={2} key={country} className="mb-3">
                                        <div className="text-center p-3 border rounded shadow-sm">
                                            <h6 className="text-muted mb-2">{country}</h6>
                                            <h3 className="mb-1 text-success">{count}</h3>
                                            <div className="progress mt-2" style={{ height: '8px' }}>
                                                <div
                                                    className="progress-bar bg-success"
                                                    role="progressbar"
                                                    style={{ width: `${percentage}%` }}
                                                    aria-valuenow={percentage}
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                ></div>
                                            </div>
                                            <small className="text-muted">{percentage}%</small>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Date de génération des statistiques */}
            {statistics.generatedAt && (
                <Card className="mb-4 border-secondary">
                    <Card.Body className="text-center">
                        <i className="fa fa-clock me-2"></i>
                        <small className="text-muted">
                            {t('productionDashboard.generatedAt') || 'Statistics generated at'}: {' '}
                            <strong>{new Date(statistics.generatedAt).toLocaleString('fr-FR', {
                                dateStyle: 'full',
                                timeStyle: 'medium'
                            })}</strong>
                        </small>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default ProductionDashboard;
